import apiClient from './axios';
import { fetchUsers } from './users';
import { normalizeSearchTerm } from './search';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function mapWalletToUser(wallet, userInfo = {}) {
  return {
    userUuid: wallet.holderUuid,
    username: wallet.user?.username ?? userInfo.username ?? '-',
    email: wallet.user?.email ?? userInfo.email ?? '-',
    wallets: [wallet],
  };
}

async function fetchCreditWalletsForUser(user) {
  const res = await apiClient.get('/v3/wallet', {
    params: {
      holder: 'User',
      holderUuid: user.uuid,
      type: 'Credit',
    },
  });

  return (res.data.wallets ?? [])
    .filter(wallet => wallet.holder === 'User' && wallet.type === 'Credit')
    .map(wallet => mapWalletToUser(wallet, user));
}

async function fetchUserByUuid(uuid) {
  try {
    const res = await apiClient.get('/v3/user', {
      params: { uuid },
    });

    return res.data;
  } catch (err) {
    return null;
  }
}

async function fetchCreditWalletByUuid(walletUuid) {
  try {
    const res = await apiClient.get('/v3/wallet', {
      params: { uuid: walletUuid },
    });

    const wallet = res.data.wallets?.[0];
    if (!wallet || wallet.holder !== 'User' || wallet.type !== 'Credit') {
      return [];
    }

    const user = await fetchUserByUuid(wallet.holderUuid);
    return [mapWalletToUser(wallet, user ?? {})];
  } catch (err) {
    return [];
  }
}

function uniqueCreditUsers(users) {
  const seen = new Set();
  const result = [];

  users.forEach((user) => {
    const walletUuid = user.wallets?.[0]?.uuid;
    const key = walletUuid || user.userUuid;
    if (!key || seen.has(key)) return;
    seen.add(key);
    result.push(user);
  });

  return result;
}

export async function fetchCredits({ offset = 0, limit = 50, search = '' } = {}) {
  // console.log(`fetchCredits ${offset} to ${offset+limit}`);
  const keyword = normalizeSearchTerm(search);

  if (keyword) {
    const userRes = await fetchUsers({
      offset,
      limit,
      search: keyword,
    });

    const users = userRes.data.users ?? [];
    const searchedUsers = (await Promise.all(
      users.map(user => fetchCreditWalletsForUser(user)),
    )).flat();

    const uuidMatches = UUID_PATTERN.test(keyword)
      ? (await Promise.all([
          fetchCreditWalletByUuid(keyword),
          fetchCreditWalletsForUser({ uuid: keyword }),
        ])).flat()
      : [];

    const mergedUsers = uniqueCreditUsers([...searchedUsers, ...uuidMatches]);
    const total = Number(userRes.data.total);
    const resultTotal = Number.isFinite(total)
      ? Math.max(total, mergedUsers.length)
      : mergedUsers.length;

    return {
      ...userRes,
      data: {
        ...userRes.data,
        total: resultTotal,
        users: mergedUsers,
      },
    };
  }

  const res = await apiClient.get('/v3/wallet', {
    params: { 
      offset: offset,
      limit: limit,
      type: 'Credit',
      holder: 'User',
    },
  });

  // console.log(`fetchCredits -> ${ JSON.stringify( res, null, 2 )}`);

  const wallets = (res.data.wallets ?? [])
    .filter(wallet => wallet.holder === 'User');

  const total = Number(res.data.total);

  return {
    ...res,
    data: {
      ...res.data,
      total: Number.isFinite(total) ? total : wallets.length,
      users: wallets.map(wallet => mapWalletToUser(wallet)),
    },
  };
}

export async function fetchCreditDetail(walletUuid, userInfo = {}) {
  const res = await apiClient.get('/v3/wallet', {
    params: {
      uuid: walletUuid,
      withTransactions: true,
    },
  });

  const wallet = res.data.wallets?.[0];

  return {
    userUuid: wallet?.holderUuid,
    username: wallet?.user?.username ?? userInfo.username ?? '-',
    email: wallet?.user?.email ?? userInfo.email ?? '-',
    wallets: wallet ? [wallet] : [],
    transactions: wallet?.ledger?.transactions ?? [],
  };
}

export async function deductCredit({ userUuid, amount, deductionReason }) {
  const res = await apiClient.post('/v3/credit/deduct', { userUuid, amount, deductionReason });
  return res.data;
}

export async function claimCredit({ userUuid, amount }) {
  const res = await apiClient.post('/v3/credit', {
    userUuid,
    reason: 'Claim',
    amount,
  });

  return res.data;
}
